"use client";

import React from "react";
import { ShoppingBag, MapPin, Building2, CircleAlert as AlertCircle, ArrowRight, Clock, Flame, Layers } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const audienceItems = [{
  id: "ecommerce",
  title: "Small ecommerce retailers",
  icon: "ShoppingBag",
  description: "If you operate an independent online shop and want to outmanoeuvre larger retailers on long-tail product queries, software gives you actionable keyword discovery and on-page checklists. My guide on",
  linkText: "Ecommerce SEO",
  linkTo: "/EcommerceSeoPage",
  postLinkText: "shows how targeted category optimisation turns software suggestions into direct basket conversions."
}, {
  id: "local",
  title: "Local trade & service practices",
  icon: "MapPin",
  description: "For plumbers, cleaners, and regional practices aiming to capture high-intent local demand, automated audits help keep your citations consistent and Google Business Profile tuned. Learn more in my breakdown of",
  linkText: "Local SEO",
  linkTo: "/LocalSEOPage",
  postLinkText: "for capturing nearby enquiries without large monthly overheads."
}, {
  id: "b2b",
  title: "B2B firms pursuing organic leads",
  icon: "Building2",
  description: "If your sales cycle relies on buyer trust and specific informational queries, software identifies bottom-of-funnel keyword gaps you can turn into focused case studies and guides. Explore my approach to",
  linkText: "B2B SEO & Lead Generation",
  linkTo: "/B2BSeoLeadGenerationPage",
  postLinkText: "to turn research into commercial inbound leads."
}];
const limitationPoints = [{
  id: "time",
  icon: "Clock",
  title: "Under 5–10 hours per month",
  text: "Software only produces recommendations; without dedicated weekly execution, reports accumulate without moving rankings."
}, {
  id: "competition",
  icon: "Flame",
  title: "Hyper-competitive niches",
  text: "When entrenched competitors dominate the first page, generic tool scores cannot replace nuanced positioning and authority acquisition."
}, {
  id: "technical",
  icon: "Layers",
  title: "Complex technical debt",
  text: "Major platform migrations, crawl budget bottlenecks, and indexing anomalies require diagnostic senior expertise rather than automated scores."
}];
export default function Suitability() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "ShoppingBag":
        return <ShoppingBag className="size-6 text-primary" />;
      case "MapPin":
        return <MapPin className="size-6 text-primary" />;
      case "Building2":
        return <Building2 className="size-6 text-primary" />;
      case "Clock":
        return <Clock className="size-5 text-primary" />;
      case "Flame":
        return <Flame className="size-5 text-primary" />;
      case "Layers":
        return <Layers className="size-5 text-primary" />;
      default:
        return <AlertCircle className="size-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="who-diy-seo-is-for" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <div className="inline-flex items-center justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-medium uppercase tracking-wider bg-secondary text-secondary-foreground border-transparent">
              Target audiences
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground text-balance">
            Who DIY SEO software is suitable for
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            In my experience working with independent businesses across the UK, automated SEO platforms deliver their best return when paired with clear internal ownership and realistic monthly time commitments.
          </p>
        </motion.div>

        {/* 3-Card Audience Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20">
          {audienceItems.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/30 flex flex-col justify-between">
                <div>
                  <CardHeader className="pb-3 pt-6 px-6 sm:px-8">
                    <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      {getIcon(item.icon)}
                    </div>
                    <CardTitle className="text-xl font-serif font-semibold text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-6 sm:px-8 pb-6 text-sm sm:text-base leading-relaxed text-muted-foreground">
                    <p>
                      {item.description}{" "}
                      <Link to={item.linkTo} className="font-medium text-primary underline underline-offset-4 hover:opacity-80 transition-opacity inline-flex items-center gap-0.5">
                        {item.linkText}
                      </Link>{" "}
                      {item.postLinkText}
                    </p>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Closing Subsection: When DIY SEO Software Isn't Enough */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 lg:p-12 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-xs sm:text-sm uppercase tracking-wider mb-2">
                <AlertCircle className="size-4" />
                <span>Consultant insight</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-foreground tracking-tight">
                When DIY SEO software isn't enough
              </h3>
              <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                Platforms identify patterns, but they cannot devise business strategy or resolve intricate technical roadblocks. Bringing in a senior consultant alongside your toolstack ensures time and capital are directed at the right opportunities.
              </p>

              {/* Specific Limitation Points */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {limitationPoints.map((point, idx) => <div key={point.id} data-index={idx} className="rounded-lg bg-card p-4 border border-border/70">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="size-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                        {getIcon(point.icon)}
                      </div>
                      <h4 className="font-semibold text-xs sm:text-sm text-foreground">
                        {point.title}
                      </h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {point.text}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* CTA action */}
            <div className="flex flex-col items-start lg:items-end justify-center shrink-0 pt-4 lg:pt-0 border-t lg:border-t-0 border-border">
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 text-left lg:text-right">
                Need direct guidance on your strategy?
              </p>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium px-6">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}